import React from 'react';
import { motion } from 'framer-motion';

const AnimatedGraphics: React.FC = () => {
  return (
    <div className="absolute inset-0 overflow-hidden">
      {/* Network Nodes */}
      <svg className="absolute inset-0 w-full h-full" viewBox="0 0 1200 800">
        {/* Animated Lines */}
        <motion.path
          d="M100,400 Q300,200 500,400 T900,400"
          stroke="currentColor"
          strokeWidth="2"
          fill="none"
          className="text-accent-300"
          initial={{ pathLength: 0, opacity: 0 }}
          animate={{ pathLength: 1, opacity: 0.6 }}
          transition={{ duration: 3, delay: 0.5 }}
        />
        
        <motion.path
          d="M200,600 Q400,300 600,500 T1000,300"
          stroke="currentColor"
          strokeWidth="2"
          fill="none"
          className="text-ocean-300"
          initial={{ pathLength: 0, opacity: 0 }}
          animate={{ pathLength: 1, opacity: 0.4 }}
          transition={{ duration: 4, delay: 1 }}
        />

        <motion.path
          d="M0,200 Q200,100 400,200 T800,200"
          stroke="currentColor"
          strokeWidth="2"
          fill="none"
          className="text-sage-300"
          initial={{ pathLength: 0, opacity: 0 }}
          animate={{ pathLength: 1, opacity: 0.3 }}
          transition={{ duration: 5, delay: 1.5 }}
        />

        {/* Animated Dots */}
        {[
          { x: 100, y: 400, delay: 0 },
          { x: 300, y: 300, delay: 0.5 },
          { x: 500, y: 400, delay: 1 },
          { x: 700, y: 350, delay: 1.5 },
          { x: 900, y: 400, delay: 2 },
        ].map((dot, i) => (
          <motion.circle
            key={i}
            cx={dot.x}
            cy={dot.y}
            r={4}
            fill="currentColor"
            className="text-accent-500"
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 0.8 }}
            transition={{ duration: 0.6, delay: dot.delay + 2 }}
          />
        ))}

        {/* Pulsing Circles */}
        {[
          { x: 200, y: 150, delay: 0 },
          { x: 800, y: 600, delay: 1 },
          { x: 1000, y: 200, delay: 2 },
        ].map((circle, i) => (
          <motion.circle
            key={`pulse-${i}`}
            cx={circle.x}
            cy={circle.y}
            r={20}
            stroke="currentColor"
            strokeWidth="2"
            fill="none"
            className="text-ocean-400"
            initial={{ scale: 0, opacity: 0 }}
            animate={{ 
              scale: [0, 1.5, 0], 
              opacity: [0, 0.6, 0] 
            }}
            transition={{
              duration: 3,
              delay: circle.delay,
              repeat: Infinity,
              repeatDelay: 2
            }}
          />
        ))}
      </svg>

      {/* Floating Geometric Shapes */}
      <motion.div
        initial={{ x: -100, y: 100, rotate: 0 }}
        animate={{ 
          x: 100, 
          y: 200, 
          rotate: 360 
        }}
        transition={{
          duration: 20,
          repeat: Infinity,
          repeatType: "reverse",
          ease: "linear"
        }}
        className="absolute top-20 left-10 w-8 h-8 border-2 border-accent-400 opacity-60"
      />

      <motion.div
        initial={{ x: 1200, y: 300, rotate: 0 }}
        animate={{ 
          x: 1000, 
          y: 500, 
          rotate: -360 
        }}
        transition={{
          duration: 25,
          repeat: Infinity,
          repeatType: "reverse",
          ease: "linear"
        }}
        className="absolute top-40 right-10 w-6 h-6 bg-ocean-400 opacity-50"
      />

      <motion.div
        initial={{ x: 600, y: -50, rotate: 0 }}
        animate={{ 
          x: 400, 
          y: 100, 
          rotate: 180 
        }}
        transition={{
          duration: 30,
          repeat: Infinity,
          repeatType: "reverse",
          ease: "linear"
        }}
        className="absolute top-0 left-1/2 w-4 h-8 bg-sage-400 opacity-40"
        style={{ clipPath: 'polygon(50% 0%, 0% 100%, 100% 100%)' }}
      />

      {/* Data Flow Animation */}
      <div className="absolute top-1/2 left-1/4 transform -translate-y-1/2">
        {[0, 1, 2, 3, 4].map((i) => (
          <motion.div
            key={`flow-${i}`}
            initial={{ x: 0, opacity: 0 }}
            animate={{ 
              x: 400, 
              opacity: [0, 1, 0] 
            }}
            transition={{
              duration: 2,
              delay: i * 0.3,
              repeat: Infinity,
              repeatDelay: 3
            }}
            className="absolute w-2 h-2 bg-accent-500 rounded-full"
            style={{ top: i * 8 }}
          />
        ))}
      </div>

      {/* Grid Pattern */}
      <svg className="absolute inset-0 w-full h-full opacity-20" viewBox="0 0 1200 800">
        <defs>
          <pattern id="grid" width="50" height="50" patternUnits="userSpaceOnUse">
            <path d="M 50 0 L 0 0 0 50" fill="none" stroke="currentColor" strokeWidth="1" className="text-ink-300"/>
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#grid)" />
      </svg>
    </div>
  );
};

export default AnimatedGraphics;